import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AddUtilityResponseModel,
  BidConfigResponse,
  UtilityConfigResponse,
  UtilityResponseModel,
  UtilityStateResponse,
  MatchingAlgorithmResponse,
} from '../interfaces/responses/models';
import { AddUtilityRequestModel } from '../interfaces/requests/models';
import { BidType, StatisticsResponse } from '../models/api-response.models';

export interface ChartPoint {
  date: string;
  buy_energy_amount: number;
  sell_energy_amount: number;
  total_energy_amount: number;
  buy_volume: number;
  sell_volume: number;
  total_volume: number;
}
export interface BidHistoryItem {
  id: string;
  participant: string;
  price: number;
  amount: number;
  status: string;
  date: string;
  user_type: string;
  bid_type: string;
}

export interface BidHistoryResponse {
  items: BidHistoryItem[];
  total_page: number;
  total_items: number;
  current_page: number;
}
export interface SettlementItem {
  customer_name: string;
  bid_type: string;
  bid_price: number;
  units: number;
  strike_price: number;
  allotted_units: number;
  untraded_units: number;
}

export interface SettlementResponse {
  items: SettlementItem[];
}

export interface DashboardResponse {
  average_price: number;
  total_cbdc_transactions: number;
  total_traded_value: number;
  total_number_of_consumers: number;
  total_number_of_prosumers: number;
  total_number_of_users: number;
}

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  private apiUrl = environment.apiUrl;
  private logoCache: Map<string, string> = new Map();
  private logoPending: Map<string, Observable<string>> = new Map();


  constructor(private http: HttpClient) { }

  private createCompleteUrl(path: string): string {
    return `${this.apiUrl}${path}`;
  }

  getUtilityList(params: any): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}utilities`, { params });
  }

  getUtilityLogo(utility_id: any): Observable<Blob> {
    const params = new HttpParams().set('device', 'web');
    return this.http.get(this.createCompleteUrl(`utilities/${utility_id}/logo`), {
      responseType: 'blob',
      params
    });
  }

  /**
   * DRY helper — fetches logo and converts blob to a data URL.
   * Returns the placeholder asset path on any error (404, no ID, empty blob).
   * Use this instead of duplicating FileReader logic in each component.
   */
  loadLogoDataUrl(utilityId: string, placeholder = 'assets/utility_logo.png'): Observable<string> {
    if (!utilityId) return of(placeholder);

    // 1. Check completed cache
    if (this.logoCache.has(utilityId)) {
      return of(this.logoCache.get(utilityId)!);
    }

    // 2. Check if a request is already pending for this ID
    if (this.logoPending.has(utilityId)) {
      return this.logoPending.get(utilityId)!;
    }

    // 3. Start new request
    const request = new Observable<string>(observer => {
      this.getUtilityLogo(utilityId).subscribe({
        next: (blob: Blob) => {
          if (!blob || blob.size === 0) {
            this.logoCache.set(utilityId, placeholder);
            observer.next(placeholder);
            observer.complete();
            return;
          }

          const reader = new FileReader();
          reader.onload = () => {
            const res = reader.result as string;
            this.logoCache.set(utilityId, res);
            observer.next(res);
            observer.complete();
          };
          reader.onerror = () => {
            this.logoCache.set(utilityId, placeholder);
            observer.next(placeholder);
            observer.complete();
          };
          reader.readAsDataURL(blob);
        },
        error: () => {
          this.logoCache.set(utilityId, placeholder);
          observer.next(placeholder);
          observer.complete();
        }
      });
    }).pipe(
      shareReplay(1),
      finalize(() => this.logoPending.delete(utilityId))
    ) as Observable<string>;

    this.logoPending.set(utilityId, request);
    return request;
  }



  getUtlityByState(state_id: any): Observable<UtilityResponseModel> {
    return this.http.get<UtilityResponseModel>(this.createCompleteUrl(`utilities/state/${state_id}`));
  }

  addUtility(payload: AddUtilityRequestModel): Observable<AddUtilityResponseModel> {
    return this.http.post<AddUtilityResponseModel>(this.createCompleteUrl(`utilities`), payload);
  }

  uploadUtilityLogo(utilityId: string, logoFile: File): Observable<any> {
    const formData = new FormData();
    formData.append('logo_file', logoFile);
    const params = new HttpParams().set('device', 'web');
    return this.http.post<any>(this.createCompleteUrl(`utilities/${utilityId}/logo`), formData, { params });
  }

  updateUtilityConfig(utility_id: string, body: any): Observable<any> {
    return this.http.put<any>(this.createCompleteUrl(`utilities/configuration/${utility_id}`), body);
  }

  updateBidConfig(utility_id: string, bid_type: number, body: any): Observable<any> {
    return this.http.put<any>(this.createCompleteUrl(`utilities/${bid_type}/configuration/${utility_id}`), body);
  }

  getMatchingAlgorithm(): Observable<MatchingAlgorithmResponse> {
    return this.http.get<MatchingAlgorithmResponse>(this.createCompleteUrl(`utilities/algorithm/matching`));
  }

  updateUtility(utility_id: string, data: any): Observable<any> {
    return this.http.put<any>(this.createCompleteUrl(`utilities/${utility_id}`), data);
  }

  getUtility(utility_id: string): Observable<UtilityStateResponse> {
    return this.http.get<UtilityStateResponse>(this.createCompleteUrl(`utilities/${utility_id}`));
  }

  deleteUtility(utility_id: string): Observable<any> {
    return this.http.delete<any>(this.createCompleteUrl(`utilities/${utility_id}`));
  }

  getUtilityConfiguration(utility_id: string): Observable<any> {
    const params = new HttpParams().set('_ts', String(Date.now()));
    return this.http.get<any>(this.createCompleteUrl(`utilities/configuration/${utility_id}`), { params });
  }

  getUtilityBidConfiguration(utility_id: string): Observable<BidConfigResponse> {
    const params = new HttpParams().set('_ts', String(Date.now()));
    return this.http.get<BidConfigResponse>(this.createCompleteUrl(`utilities/bid/configuration/${utility_id}`), { params });
  }

  getImportExport(offset: number = 0): Observable<any> {
    const url = this.createCompleteUrl(`mdm/energy/import-export`);
    let params = new HttpParams().set('offset', String(offset)).set('_ts', String(Date.now())); // cache buster
    return this.http.get<any>(url, { params });
  }

  // New Dashboard API (Historical totals)
  getUtilityDashboard(): Observable<DashboardResponse> {
    const url = this.createCompleteUrl(`p2p/utility/dashboard`);
    return this.http.get<DashboardResponse>(url);
  }

  // Old Statistics API (Round-specific data)
  getUtilityStatistics(
    start_date: string,
    end_date: string,
    bid_type: BidType = BidType.Preferential
  ): Observable<StatisticsResponse> {
    const url = this.createCompleteUrl(`p2p/utility/statistics`);
    const body = { start_date, end_date, bid_type };
    return this.http.post<StatisticsResponse>(url, body);
  }

  getChart(
    utility_id: string,
    start_date: string,
    end_date: string,
    bid_type: BidType = BidType.Preferential
  ): Observable<ChartPoint[]> {
    const url = this.createCompleteUrl(`p2p/utility/statistics/chart`);
    const body = { utility_id, start_date, end_date, bid_type };
    console.debug('[UtilityChartService] POST', url, body);
    return this.http.post<ChartPoint[]>(url, body);
  }

  getBidHistory(
    utility_id: string,
    start_date: string,
    end_date: string,
    limit: number,
    offset: number,
    bid_type?: string
  ): Observable<BidHistoryResponse> {
    const url = this.createCompleteUrl(`p2p/utility/statistics/history`);

    let params = new HttpParams()
      .set('utility_id', utility_id)
      .set('start_date', start_date)
      .set('end_date', end_date)
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    if (bid_type && bid_type.toLowerCase() !== 'all') {
      params = params.set('bid_type', bid_type);
    }

    console.debug('[UtilityStatisticsService] GET', url, { params });
    return this.http.get<BidHistoryResponse>(url, { params });
  }

  getSettlementHistory(utility_id: string, start_date: string, end_date: string): Observable<SettlementResponse> {
    const url = this.createCompleteUrl(`p2p/utility/statistics/settlement`);
    const body = { utility_id, start_date, end_date };
    console.debug('[UtilityStatisticsService] POST', url, body);
    return this.http.post<SettlementResponse>(url, body);
  }
}
